<template>

  <div 
    class     = "annotations-pane-container d-flex flex-column"
    v-hotkey  = "keymap"
  >

    <div 
      v-if    = "showAnnotations"
      class   = "flex-grow-1 d-flex flex-column"
    >

      <!-- Annotations -->
      <div class="annotations flex-grow-1">
        <div 
          class = "annotations-list"
          ref   = "annotations_list"
        >
          <AnnotationItem
            v-for       = "(annotation, index) in sortedAnnotations"
            :key        = "index"
            :index      = "index"
            :annotation = "annotation"
            :ref        = "annotation.id"
          >
          </AnnotationItem>
        </div>
      </div>

      <!-- Tools -->
      <v-toolbar
        class     = "flex-grow-0"
        dense
        elevation = "0"
        color     = "rgba(0, 0, 0, 0.3)"
      >

        <v-spacer></v-spacer>

        <!-- See Tools -->
        <ToolbarButton
          tooltip   = "Show Tools"
          hotkey    = "d"
          @click    = "onToggleTools"
        >
          <v-icon :color="ui.areAnnotationToolsShown ? 'primary' : 'default'">mdi-comment-edit-outline</v-icon>
        </ToolbarButton>

        <v-divider 
          class     = "mx-2"
          vertical
        >
        </v-divider>

        <!-- Add Annotation -->
        <ToolbarButton
          tooltip   = "Add Annotation"
          hotkey    = "a"
          :disabled = "!canAddAnnotation"
          @click    = "onAddAnnotation"
        >
          <v-icon>mdi-comment-plus-outline</v-icon>
        </ToolbarButton>

        <!-- Clear All Annotations -->
        <ToolbarButton
          tooltip   = "Clear Annotations"
          :disabled = "!hasAnnotations"
          @click    = "onClearAnnotations"
        >
          <v-icon>mdi-comment-remove-outline</v-icon>
        </ToolbarButton>

        <v-divider 
          class     = "mx-2"
          vertical
        >
        </v-divider>

        <!-- Import Annotations -->
        <ToolbarButton
          tooltip   = "Import Annotations"
          @click    = "onImport"
        >
          <v-icon>mdi-import</v-icon>
        </ToolbarButton>

        <input 
          ref     = "import_input"
          class   = "d-none"
          type    = "file"
          accept  = ".json"
          
          @input  = "onFileSelected"
        >

        <!-- Export Annotations -->
        <ToolbarButton
          tooltip   = "Export Annotations"
          :disabled = "!hasAnnotations"
          @click    = "onExport"
        >
          <v-icon>mdi-export</v-icon>
        </ToolbarButton>

      </v-toolbar>

    </div>

    <ConfirmationDialog ref="confirmation_dialog"></ConfirmationDialog>

  </div>

</template>

<script>

// #region Imports

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import ToolbarButton from './components/buttons/ToolbarButton';
import AnnotationItem from './components/annotations/AnnotationItem';
import ConfirmationDialog from './components/dialogs/ConfirmationDialog';
import { 
  ADD_ANNOTATION, 
  CLEAR_ANNOTATIONS,
  IMPORT_ANNOTATIONS,
  EXPORT_ANNOTATIONS,
  PAUSE_VIDEO,
} from '@/utils/action-types';
import {
  SET_ANNOTATION_TOOLS_ARE_SHOWN,
  SET_ANNOTATIONS_REFERENCES
} from '@/utils/mutation-types';

// #endregion Imports

export default {

  // #region Properties

  name:       'AnnotationsPane',
  components: {
    AnnotationItem,
    ConfirmationDialog,
    ToolbarButton
  },
  computed: {
    ...mapState([
      'annotations',
      'ui',
      'video'
    ]),

    keymap() {
      return {
        'd': () => this.onToggleTools(),
        'a': () => this.onAddAnnotation()
      }; 
    },
    sortedAnnotations() {
      return [...this.annotations.annotations].sort((a, b) => a.frame - b.frame);
    },
    showAnnotations() {
      return this.video.canPlay;
    },
    canAddAnnotation() {
      return this.video.canPlay;
    },
    hasAnnotations() {
      return this.annotations.annotations.length > 0;
    }
  },

  // #endregion Properties
  
  // #region Life-Cycle

  mounted() {
    this.$store.commit(SET_ANNOTATIONS_REFERENCES, this.$refs);
  },

  // #endregion Life-Cycle

  // #region Methods

  methods: {

    // #region Events
    
    onToggleTools() {
      this.$store.commit(SET_ANNOTATION_TOOLS_ARE_SHOWN, !this.ui.areAnnotationToolsShown);
    },

    onAddAnnotation() {
      this.$store.dispatch(PAUSE_VIDEO);
      this.$store.dispatch(ADD_ANNOTATION);
    },

    async onClearAnnotations() {
      if (await this.$refs.confirmation_dialog.confirm(
        'Remove All Annotations?',
        'Are you sure you want remove all annotations?',
        'Remove Annotations',
        'error',
        'Keep Annotations',
        'primary'
      )) {
        this.$store.dispatch(CLEAR_ANNOTATIONS);
      }
    },

    onImport() {
      this.$refs.import_input.click();
    },

    onFileSelected() {
      const file = this.$refs.import_input.files[0];
      if (!file) {
        return;
      }

      this.$store.dispatch(IMPORT_ANNOTATIONS, file);
      this.$refs.import_input.value = '';
    },

    onExport() {
      this.$store.dispatch(EXPORT_ANNOTATIONS);
    }

    // #endregion Events

  }

  // #endregion Methods

};

</script>

<style lang="scss" scoped>

.annotations-pane-container {
  height: 100%;
  
  .annotations {
    position:   relative;

    .annotations-list {
      position:       absolute;
      top:            0px;
      right:          0px;
      bottom:         0px;
      left:           0px;
      overflow:       auto;
    }
  }
}

</style>